import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Bell, Loader2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { enGB, es } from 'date-fns/locale'
import { useNotifications } from '../hooks/useNotifications'
import { useToast } from '@/components/ui/use-toast'

const NOTIFICATION_META_KEYS = Object.freeze({
  "golf": "portalPresentation.departments.golf",
  "spa": "portalPresentation.departments.spa",
  "fitness": "portalPresentation.departments.fitness",
  "racquet": "portalPresentation.departments.racquet",
  "reciprocal access": "portalPresentation.member_reciprocal_access",
  "booking created": "racquet.toasts.bookingCreated",
  "booking updated": "racquet.toasts.bookingUpdated",
  "booking cancelled": "portalPresentation.member_booking_cancelled",
  "spa_booking_created": "racquet.toasts.bookingCreated",
  "spa_booking_updated": "racquet.toasts.bookingUpdated",
  "spa_booking_cancelled": "portalPresentation.member_booking_cancelled",
  "reciprocal_access_request_approved": "portalPresentation.member_visit_request_approved"
})

export default function NotificationBell({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const {t} = useOptionalPrivanaI18n();
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const listRef = useRef(null)
  const { toast } = useToast()
  const { notifications, unreadCount, isLoading, reload, markRead, loadMore, isLoadingMore, hasMore, clearAll, isClearingAll } = useNotifications({ clubCode })

  const items = useMemo(() => {
    if (!notifications?.length) return []
    return notifications.map((notification) => {
      const createdAtRaw = notification?.created_at || notification?.createdAt
      const createdAt = createdAtRaw ? new Date(createdAtRaw) : null
      const timeAgo = createdAt ? formatDistanceToNow(createdAt, { addSuffix: true, locale: locale === 'es' ? es : enGB }) : ''
      const metaLabel = [notification.category, notification.type]
        .filter(Boolean)
        .map((part) => NOTIFICATION_META_KEYS[part] ? memberText(NOTIFICATION_META_KEYS[part]) : String(part).replace(/_/g, ' '))
        .join(' · ')

      return {
        id: notification.id,
        title: notification.title || memberText("portalPresentation.member_notification"),
        body: notification.category==='academy'?t(`academyP2.events.${notification.type}`):notification.body || '',
        isRead: Boolean(notification.is_read || notification.isRead),
        timeAgo,
        metaLabel,
      }
    })
  }, [notifications,t])

  useEffect(() => {
    const handleClickAway = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [])

  const handleToggle = () => {
    setOpen((prev) => !prev)
    if (!open) {
      reload?.()
    }
  }

  const handleClick = (notificationId) => {
    markRead?.(notificationId)
  }

  const handleScroll = (event) => {
    const target = event.currentTarget
    if (!target || !hasMore || isLoadingMore) return
    const threshold = 48
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    if (distanceFromBottom <= threshold) {
      loadMore?.()
    }
  }

  const handleClearAll = async () => {
    const ok = await clearAll?.()
    if (!ok) {
      toast({
        title: memberText("portalPresentation.member_unable_to_clear_notifications"),
        description: memberText("portalPresentation.member_please_try_again"),
        variant: 'destructive',
      })
    }
  }

  const refreshDisabled = isLoading || isLoadingMore || isClearingAll
  const clearAllDisabled = isLoading || isLoadingMore || isClearingAll || unreadCount === 0

  useEffect(() => {
    if (!open || !hasMore || isLoading || isLoadingMore) return
    const listEl = listRef.current
    if (!listEl) return
    if (listEl.scrollHeight <= listEl.clientHeight) {
      loadMore?.()
    }
  }, [open, hasMore, isLoading, isLoadingMore, items.length, loadMore])

  return (
    <div className="notification-button" ref={containerRef}>
      <button
        type="button"
        className="icon-button"
        aria-label={memberText("portalPresentation.member_notifications")}
        onClick={handleToggle}
      >
        <Bell size={18} />
        {unreadCount > 0 ? (
          <span className="notification-badge" aria-hidden>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="notification-panel">
          <div className="notification-header">
            <div>
              <div className="notification-title">{memberText("portalPresentation.member_notifications")}</div>
              <div className="notification-subtitle">
                {unreadCount ? memberText("portalPresentation.member__count_new", { count: unreadCount }) : memberText("portalPresentation.member_all_caught_up")}
              </div>
            </div>
            <div className="notification-actions">
              <button
                className="notification-refresh"
                type="button"
                onClick={reload}
                disabled={refreshDisabled}
              >
                {isLoading ? (
                  <span className="notification-loading">
                    <Loader2 size={14} className="spin" /> {memberText("portalPresentation.member_loading")} </span>
                ) : (
                  memberText("portalPresentation.member_refresh")
                )}
              </button>
              <button
                className="notification-refresh"
                type="button"
                onClick={handleClearAll}
                disabled={clearAllDisabled}
              >
                {isClearingAll ? (
                  <span className="notification-loading">
                    <Loader2 size={14} className="spin" /> {memberText("portalPresentation.member_clearing")} </span>
                ) : (
                  memberText("portalPresentation.member_clear_all")
                )}
              </button>
            </div>
          </div>
          <div className="notification-list" role="list" onScroll={handleScroll} ref={listRef}>
            {isLoading ? (
              <div className="notification-empty">{memberText("portalPresentation.member_loading_notifications")}</div>
            ) : items.length ? (
              <>
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`notification-item${item.isRead ? '' : ' unread'}`}
                    onClick={() => handleClick(item.id)}
                  >
                    <div className="notification-item-title">{item.title}</div>
                    <div className="notification-item-meta">
                      {item.metaLabel ? `${item.metaLabel} • ${item.timeAgo}` : item.timeAgo}
                    </div>
                    {item.body ? <div className="notification-item-body">{item.body}</div> : null}
                  </button>
                ))}
                {isLoadingMore ? (
                  <div className="notification-more-loading">
                    <Loader2 size={14} className="spin" />
                  </div>
                ) : null}
              </>
            ) : (
              <div className="notification-empty">{memberText("portalPresentation.member_no_notifications_yet")}</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
